import type { RootState } from "../state";
import message from "@/utils/message/messageFactory";
import { isAppleLike, setIOSSwitchLoading } from "./iosSwitchToast";
import { switchProducts } from "@/api/live";
import { listProducts } from "@/api/myProject";
import { $t } from "@/plugins/i18n";
// 商品相关方法

function isAndroidChromeBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (!/Android/i.test(ua)) return false;
  if (!/Chrome\/\d+/i.test(ua)) return false;
  // 排除常见基于 Chrome 的其它壳浏览器
  if (/Edg|Edge|OPR|OPiOS|SamsungBrowser|UCBrowser|QQBrowser/i.test(ua)) {
    return false;
  }
  return true;
}

function isUniversalWebAudioRoom(roomId?: string): boolean {
  if (!roomId) return false;
  try {
    const g: any = globalThis as any;
    const rooms = g?.__universalWebAudioRooms;
    return !!(rooms && typeof rooms.has === "function" && rooms.has(roomId));
  } catch {
    return false;
  }
}

export async function selectProduct(
  this: RootState & { [key: string]: any },
  params: { id: string; index: number }
) {
  if (!this.currentRoomState) return;
  const state = this.currentRoomState;
  const { id, index } = params;

  try {
    (state as any).__switchGuardPid = null;
    (state as any).__switchGuardVoiceNo = null;
  } catch {}

  // 检查是否尝试切换到当前已选中的产品
  if (state.selectedProductId === id) {
    message.warning($t("msg.currentProductPlayingNoRepeatSwitch"), true);
    return;
  }

  // 修改：等待当前音频播放完成后再切换产品
  // 注释产品切换准备日志，保留重要错误和成功信息
  // console.log(`🔄 准备切换产品: ${id}`);

  state.cardLoadingMap[id] = true;
  try {
    setIOSSwitchLoading.call(this, id, true);
  } catch {}

  const roomId = state.roomId || state.productList?.id || this.currentRoomId;

  // 修改：立即调用API，但根据播放状态决定何时更新界面
  // 非苹果端：Howler 播放器判断；苹果端：HTML5 链式/WA 状态判断
  const howlerPlaying = !!(state.isPlaying && (state as any).howlerPlayer);
  const appleChain = (state as any).__appleHtml5Chain__;
  const appleWA = (state as any).__appleWA__;
  const appleChainPlaying = !!(appleChain && (appleChain as any).playing);
  let appleWAPlaying = false;
  try {
    appleWAPlaying = !!(
      appleWA &&
      appleWA.player &&
      appleWA.player.getStatus &&
      appleWA.player.getStatus().isPlaying
    );
  } catch {}

  let universalWAPlaying = false;
  try {
    const universalPlayer = (state as any).universalWebAudioPlayer;
    if (universalPlayer && typeof universalPlayer.getStatus === "function") {
      const status = universalPlayer.getStatus();
      universalWAPlaying = !!(status && status.isPlaying);
    }
  } catch {}

  const isAndroidChromeUniversal = !!(
    roomId &&
    isAndroidChromeBrowser() &&
    isUniversalWebAudioRoom(roomId)
  );

  let isCurrentlyPlaying =
    howlerPlaying || appleChainPlaying || appleWAPlaying || universalWAPlaying;

  if (!isCurrentlyPlaying && isAndroidChromeUniversal) {
    const queueLen = Array.isArray(state.queue) ? state.queue.length : 0;
    if (queueLen > 0) {
      isCurrentlyPlaying = true;
      try {
        console.log(
          `[switch] override playing state for Android Chrome UniversalWebAudio queue=${queueLen}`
        );
      } catch {}
    }
  }

  try {
    console.log("[switch] playingStates", {
      howlerPlaying,
      appleChainPlaying,
      appleWAPlaying,
      universalWAPlaying,
      decided: isCurrentlyPlaying,
      roomId,
      selectedProductId: state.selectedProductId,
      switchVersion: state.switchVersion
    });
  } catch {}

  // 准备切换参数
  if (!roomId) {
    console.error(`❌ 无法获取roomId:`, {
      stateRoomId: state.roomId,
      productListId: state.productList?.id,
      currentRoomId: this.currentRoomId
    });
    message.error($t("msg.switchProductFailedCannotGetRoomId"), true);
    state.cardLoadingMap[id] = false;
    try {
      setIOSSwitchLoading.call(this, id, false);
    } catch {}
    return;
  }

  const switchParams = {
    roomId: roomId,
    productId: state.productList.products[index].id
  };

  // 进入新切换版本：重置版本号为1，确保每次切换产品后版本号从1开始，避免累积导致的版本跳跃
  // 这样每次产品切换后，新版本音频的version都是1，不会出现0->2的跳跃
  state.switchVersion = 1;

  if (isCurrentlyPlaying) {
    // 如果有音频正在播放：后台预解码目标产品，保持当前产品连续播放；边界就绪后再切换
    console.log(
      `🔄 当前有音频播放中：进入"准备切换"，旧产品继续播放，后台预解码目标产品`
    );

    // 处理Apple WebAudio
    try {
      const wa = (state as any).__appleWA__;
      if (wa) {
        // 切换准备：清空 WA 的去重集合和版本映射，避免旧版本/已清理条目残留导致目标首句被误判重复
        try {
          if ((wa as any).addedIds) {
            (wa as any).addedIds.clear();
            (wa as any).__dedupResetPid = id;
          }
          // 同时清空版本映射，确保产品切换后重新记录版本号
          if ((wa as any).idVersionMap) {
            (wa as any).idVersionMap.clear();
          }
        } catch {}
        const prev = (wa as any).preparingTargetPid || null;
        // 取消旧目标的高优先级解码队列任务（如果有）
        try {
          const p: any = wa.player;
          if (prev && p && typeof p.invalidateQueuedByTag === "function") {
            p.invalidateQueuedByTag(`TARGET_${prev}`);
          }
        } catch {}
        // 轻防抖：合并极短时间内的多次点击，仅以最后一次为准
        try {
          if ((wa as any).__prepareDebounceTimer)
            clearTimeout((wa as any).__prepareDebounceTimer);
        } catch {}
        (wa as any).__prepareDebounceTimer = setTimeout(() => {
          (wa as any).preparingTargetPid = id;
          (wa as any).targetPrepared = false;
          (wa as any).targetDecoding = false;
          (wa as any).pendingTargetFeed = [];
          try {
            (wa as any).switchTargetReady = false;
          } catch {}
          console.log(
            `[switch] apple-wa preparingTargetPid=${id} (reset targetPrepared/switchTargetReady)`
          );
        }, 150);
      }
    } catch (e) {
      console.warn("[switch] mark preparingTargetPid error", e);
    }

    // 处理UniversalWebAudio
    try {
      const universalPlayer = (state as any).universalWebAudioPlayer;
      if (universalPlayer) {
        console.log(`[UniversalWA] 标记准备切换产品: ${id}`);

        // 设置准备切换的目标产品
        universalPlayer.setPreparingTargetPid(id);

        // 清理防抖定时器
        if ((state as any).__universalPrepareTimer) {
          clearTimeout((state as any).__universalPrepareTimer);
        }

        (state as any).__universalPrepareTimer = setTimeout(() => {
          console.log(`[UniversalWA] 防抖后确认切换到产品: ${id}`);
        }, 150);
      }
    } catch (e) {
      console.warn("[UniversalWA] 设置准备切换产品失败", e);
    }

    message.info("正在切换产品：后台准备目标产品，当前内容不受影响");

    // 立即调用API
    try {
      const switchResult = await switchProducts(switchParams);
      if (switchResult && switchResult.success) {
        // ios 停止当前播放并清空队列
        if (state.__appleWA__ && state.__appleWA__.player) {
          state.queue = [];
          state.__appleWA__.player.stop();
          // 清理产品切换相关状态
          state.__appleWA__.preparingTargetPid = null;
          state.__appleWA__.targetPrepared = false;
        }
        // 安卓清空队列
        if (state && (state as any).universalWebAudioPlayer) {
          state.queue = [];
          (state as any).universalWebAudioPlayer.stop();
        }

        // 防护极端时机：API完成后再次检查播放状态
        const howlerStillPlaying = !!(
          state.isPlaying &&
          (state as any).howlerPlayer &&
          (state as any).howlerPlayer.getStatus?.().isPlaying
        );
        const appleChain = (state as any).__appleHtml5Chain__;
        const appleWA = (state as any).__appleWA__;
        const appleChainStillPlaying = !!(
          appleChain && (appleChain as any).playing
        );
        let appleWAStillPlaying = false;
        try {
          appleWAStillPlaying = !!(
            appleWA &&
            appleWA.player &&
            appleWA.player.getStatus &&
            appleWA.player.getStatus().isPlaying
          );
        } catch {}
        let universalWAStillPlaying = false;
        try {
          const universalPlayer = (state as any).universalWebAudioPlayer;
          if (
            universalPlayer &&
            typeof universalPlayer.getStatus === "function"
          ) {
            const status = universalPlayer.getStatus();
            universalWAStillPlaying = !!(status && status.isPlaying);
          }
        } catch {}
        let isStillPlaying =
          howlerStillPlaying ||
          appleChainStillPlaying ||
          appleWAStillPlaying ||
          universalWAStillPlaying;

        if (!isStillPlaying && isAndroidChromeUniversal) {
          isStillPlaying = true;
          try {
            console.log(
              `[switch] force wait for boundary on Android Chrome UniversalWebAudio pid=${id}`
            );
          } catch {}
        }
        state.selectedProductId = id;
        state.productDetail = state.productList.products[index];
        setIOSSwitchLoading.call(this, id, false);

        if (!isStillPlaying) {
          // 🚨 极端时机检测：音频已经在API调用期间结束，直接执行切换
          try {
            console.log(
              `[apple-switch] 极端时机检测：音频已结束，直接切换 pid=${id}`
            );
          } catch {}

          // 清理Apple WebAudio准备标记
          try {
            const wa = (state as any).__appleWA__;
            if (wa) {
              (wa as any).preparingTargetPid = null;
              (wa as any).targetPrepared = false;
              (wa as any).pendingTargetFeed = [];
            }
          } catch {}

          // 清理UniversalWebAudio准备标记
          try {
            const universalPlayer = (state as any).universalWebAudioPlayer;
            if (universalPlayer) {
              universalPlayer.clearSwitchState();
            }
          } catch {}

          // 直接执行完整切换逻辑
          this.executePendingProductSwitch(roomId);
          return;
        }

        // console.log(`✅ API切换产品成功，设置待更新状态`);
        // 设置待切换状态，等播放完成后更新界面
        state.pendingProductSwitch = {
          productId: id,
          productIndex: index,
          switchParams: switchParams,
          apiCompleted: true, // 标记API已完成
          createdAt: Date.now() // 记录创建时间用于超时检测
        };

        const disableUniversalFallback = isAndroidChromeUniversal;

        if (!disableUniversalFallback) {
          // 设置安全超时：如果8秒内没有执行，强制执行（Apple设备可能需要更长时间）
          setTimeout(() => {
            if (
              state.pendingProductSwitch &&
              state.pendingProductSwitch.productId === id
            ) {
              try {
                console.log(`[apple-switch] pending超时强制执行 pid=${id}`);
              } catch {}
              this.executePendingProductSwitch(roomId);
            }
          }, 100);
        } else {
          try {
            console.log(
              `[switch] skip fallback timer for Android Chrome UniversalWebAudio room=${roomId}`
            );
          } catch {}
        }
      } else {
        console.error(`❌ API切换产品失败:`, switchResult);
        message.error($t("msg.switchProductFailed"), true);
        if (isAppleLike()) {
          // iOS：等待 toast 回调触发后再收起 loading
          try {
            setIOSSwitchLoading.call(this, id, true);
          } catch {}
        } else {
          state.cardLoadingMap[id] = false;
        }
        try {
          setIOSSwitchLoading.call(this, id, false);
        } catch {}
        // 失败：清理准备标记
        try {
          const wa = (state as any).__appleWA__;
          if (wa) {
            (wa as any).preparingTargetPid = null;
            (wa as any).targetPrepared = false;
            (wa as any).pendingTargetFeed = [];
          }
        } catch {}
        // 清理UniversalWebAudio准备标记
        try {
          const universalPlayer = (state as any).universalWebAudioPlayer;
          if (universalPlayer) {
            universalPlayer.clearSwitchState();
          }
        } catch {}
      }
    } catch (error) {
      console.error(`❌ 产品切换API异常:`, error);
      message.error($t("msg.switchProductFailed"), true);
      if (!isAppleLike()) {
        state.cardLoadingMap[id] = false;
      } else {
        try {
          setIOSSwitchLoading.call(this, id, true);
        } catch {}
      }
      try {
        setIOSSwitchLoading.call(this, id, false);
      } catch {}
      // 异常：清理准备标记
      try {
        const wa = (state as any).__appleWA__;
        if (wa) {
          (wa as any).preparingTargetPid = null;
          (wa as any).targetPrepared = false;
          (wa as any).pendingTargetFeed = [];
        }
      } catch {}
    }
    return;
  }

  // console.log(`🔄 没有音频播放，立即执行完整的产品切换: ${id}`);

  if (state.productList.status === "live") {
    // console.log(`🔄 开始执行直播状态的产品切换: ${id}`);

    // 修复：确保roomId存在
    const roomId = state.roomId || state.productList?.id || this.currentRoomId;
    if (!roomId) {
      console.error(`❌ 无法获取roomId:`, {
        stateRoomId: state.roomId,
        productListId: state.productList?.id,
        currentRoomId: this.currentRoomId
      });
      message.error($t("msg.switchProductFailedCannotGetRoomId"), true);
      return;
    }

    const switchParams = {
      roomId: roomId,
      productId: state.productList.products[index].id
    };
    console.log(`🔍 切换参数:`, switchParams);

    try {
      // 调用API切换产品
      // console.log(`🔄 开始调用API切换产品`);

      const switchResult = await switchProducts(switchParams);

      state.cardLoadingMap[id] = false;
      if (switchResult && switchResult.success) {
        // API调用成功，更新本地状态
        // console.log(`✅ API切换产品成功`);

        // 关键改进：彻底清理旧的Howler播放器和事件，防止频繁切换时出现状态混乱
        if ((state as any).howlerPlayer) {
          const howlerPlayer = (state as any).howlerPlayer;
          console.log(`开始清理旧播放器，房间ID: ${howlerPlayer.roomId}`);

          // 移除健康检查，直接清理，避免误判和错误修复状态
          // 在产品切换时，播放器状态可能不一致是正常的，不应该被强制修复

          try {
            // 用新的强制清理方法
            if (typeof howlerPlayer.forceCleanupAllStates === "function") {
              howlerPlayer.forceCleanupAllStates();
            }
            // 新增：重置重复检测机制，确保新产品音频能正常添加
            if ((state as any).howlerEnqueuedIds) {
              (state as any).howlerEnqueuedIds.clear();
            }
            // 新增：重置当前产品音频的播放状态，避免被跳过逻辑过滤
            // 修复：使用最新的selectedProductId而不是可能过时的switchingProductId
            const currentPid = state.selectedProductId;
            if (currentPid && state.queue && state.queue.length > 0) {
              state.queue.forEach((item: any) => {
                if (item.productId === currentPid && item.status === "played") {
                  item.status = "pending"; // 重置为待播放状态
                  item.playedCharCount = 0; // 重置播放进度
                  delete (item as any).audioTimeProgress; // 清除音频时间进度
                }
              });
              console.log(`已重置产品${currentPid}的音频播放状态`);
            }
            // 新增：清除产品切换标记，避免后续误判
            state.switchingProductId = null;
            howlerPlayer.stop();
            howlerPlayer.destroy();
          } catch (error) {
            console.error(`清理播放器失败:`, error);
          } finally {
            (state as any).howlerPlayer = null;
            console.log(`旧播放器已彻底清理`);
          }
        }

        // 清理UniversalWebAudio播放器
        if ((state as any).universalWebAudioPlayer) {
          const universalPlayer = (state as any).universalWebAudioPlayer;
          console.log(`[UniversalWA] 清理旧播放器`);

          try {
            // 清理切换状态
            universalPlayer.clearSwitchState();
            // 停止并清理
            universalPlayer.stop();
            // 清理队列
            if (
              typeof universalPlayer.purgeQueuedExceptAllowed === "function"
            ) {
              universalPlayer.setAllowedProductId(null); // 先清除限制
              universalPlayer.purgeQueuedExceptAllowed(true);
            }
          } catch (error) {
            console.error(`[UniversalWA] 清理播放器失败:`, error);
          }
        }

        // 清理所有可能的定时器和防抖状态
        if (state.lastLogTime) {
          state.lastLogTime = {};
        }

        state.selectedProductId = id;
        state.productDetail = state.productList.products[index];

        // 重置播放状态，但保留队列历史
        state.currentIndex = state.queue.length;
        state.isPlaying = false;

        // 清除待切换状态
        state.pendingProductSwitch = null;

        // 清空保存的播放时间
        state.currentAudioTime = undefined;

        // 关键：切换后重置去重集合，避免首条新产品音频被旧集合误杀
        try {
          (state as any).recentAudioIds = new Set<string>();
          (state as any).recentTextKeys = new Map<string, number>();
        } catch {}

        // 关键修复：清除队列中所有音频的播放进度，防止新产品音频被当作"恢复播放"模式
        // console.log(`清除队列中所有音频的播放进度，确保新产品从头播放`);
        state.queue.forEach(item => {
          // 清除音频时间进度相关属性
          delete (item as any).audioTimeProgress;
          delete (item as any).audioCurrentTime;
          delete (item as any).lastSavedProgress;
          // 重置播放字符计数
          item.playedCharCount = 0;
          item.status = "pending";
        });

        // 清除恢复保护期，允许接收新产品的音频
        state.lastRecoveryTime = null;

        // 设置产品切换标记，用于过滤旧产品的音频
        state.lastProductSwitchTime = Date.now();
        state.switchingProductId = id;

        // 产品切换完成，继续正常播放流程
        // console.log(`🔄 产品切换完成，接受所有新音频`);

        console.log(
          `🔄 设置产品切换标记: 新产品ID=${state.switchingProductId}, 切换参数:`,
          switchParams
        );

        // 新增：检查队列中是否有“可播项”（按版本/产品/保护期/URL过滤）
        const PROTECT_MS = Number((state as any).switchProtectMs ?? 100);
        const inProtect =
          !!state.lastProductSwitchTime &&
          Date.now() - state.lastProductSwitchTime <= PROTECT_MS;

        // 从队列头开始定位首个可播项，避免索引在队尾错过前面已可播的首句
        let probeIndex = 0;
        while (probeIndex < state.queue.length) {
          const q: any = state.queue[probeIndex];
          const currentPid =
            state.switchingProductId || state.selectedProductId || null;
          const sameProduct = !!(
            q?.productId &&
            currentPid &&
            q.productId === currentPid
          );
          const verMismatchRaw = q?.version !== (state.switchVersion ?? 0);
          const verMismatch = verMismatchRaw;
          const wrongProduct =
            state.switchingProductId &&
            q?.productId &&
            q.productId !== state.switchingProductId;
          // 保护期内仅对“无 productId”的消息使用时间阈值过滤
          const tooOldInProtect =
            inProtect &&
            !q?.productId &&
            q?.createTime &&
            q.createTime < state.lastProductSwitchTime!;
          const noUrl = !q?.audioUrl;
          if (verMismatch || wrongProduct || tooOldInProtect || noUrl) {
            probeIndex++;
            continue;
          }
          break;
        }

        if (probeIndex < state.queue.length && !state.isPlaying) {
          // console.log(`🎵 产品切换后存在可播项，推进索引到 ${probeIndex} 并开始播放`);
          state.currentIndex = probeIndex;
          state.isPlaying = true;
          setTimeout(() => {
            this.playNext(roomId);
          }, 100);
        } else {
          // console.log(`🎵 产品切换后暂无可播项，等待新音频`);
        }

        // 检查WebSocket连接状态，只在连接异常时才重新连接
        if (
          !state.audioQueueWs ||
          state.audioQueueWs.readyState !== WebSocket.OPEN
        ) {
          console.warn(`🔌 WebSocket连接异常，需要重新连接`);
          if (state.audioQueueWs) {
            state.audioQueueWs.close();
            state.audioQueueWs = null;
          }

          const roomId = state.roomId || state.productList.id;
          await this.initAudioQueueWebSocket(roomId);
          console.log(`🔌 WebSocket重新连接完成`);
        } else {
          console.log(`🔌 WebSocket连接正常，保持现有连接`);
        }

        console.log(
          `🎵 切换产品后请求新音频 - 产品: ${state.productDetail.name}`
        );

        // 关键修复：统一使用延迟显示切换成功的机制，确保与实际播放一致
        // 检查队列中是否还有旧产品音频需要过滤
        const PROTECT_MS_DIRECT = Number((state as any).switchProtectMs ?? 100);
        const inProtectDirect =
          !!state.lastProductSwitchTime &&
          Date.now() - state.lastProductSwitchTime <= PROTECT_MS_DIRECT;
        let hasOldProductAudioDirect = false;
        for (let i = state.currentIndex; i < state.queue.length; i++) {
          const q: any = state.queue[i];
          const currentPid =
            state.switchingProductId || state.selectedProductId || null;
          const sameProduct = !!(
            q?.productId &&
            currentPid &&
            q.productId === currentPid
          );
          const verMismatchRaw = q?.version !== (state.switchVersion ?? 0);
          const verMismatch = verMismatchRaw && !sameProduct;
          const wrongProduct =
            state.switchingProductId &&
            q?.productId &&
            q.productId !== state.switchingProductId;
          const tooOldInProtect =
            inProtectDirect &&
            !q?.productId &&
            q?.createTime &&
            q.createTime < state.lastProductSwitchTime!;
          const noUrl = !q?.audioUrl;
          if (verMismatch || wrongProduct || tooOldInProtect || noUrl) {
            hasOldProductAudioDirect = true;
            break;
          }
        }

        if (hasOldProductAudioDirect) {
          console.log(
            `🔄 直接切换模式：检测到队列中还有旧产品音频，延迟显示切换成功弹窗`
          );
          // 标记需要延迟显示成功弹窗
          (state as any).pendingSwitchSuccessMessage = true;
        } else {
          console.log(`✅ 直接切换模式：队列中无旧产品音频`);
          // iOS：严格策略，仅在新产品真正起播时显示（此处仅置位）
          if (isAppleLike()) {
            try {
              (state as any).pendingSwitchSuccessMessage = true;
            } catch {}
          } else {
            message.success($t("msg.switchProductSuccess"), true);
          }
        }
        if (isAppleLike()) {
          try {
            setIOSSwitchLoading.call(this, id, true);
          } catch {}
        } else {
          state.cardLoadingMap[id] = false;
        }
        // 切换成功后触发一次前台预填：可见态保守预填 1 条（隐藏态将由 onended 再补）
        try {
          const room =
            state.roomId || state.productList?.id || this.currentRoomId;
          (this as any).augmentStoreWithAppleHiddenWebAudio &&
            (this as any).augmentStoreWithAppleHiddenWebAudio(this as any);
          (this as any).prefillAppleWAHead &&
            (this as any).prefillAppleWAHead(room, 1);
        } catch {}
      } else {
        // API调用失败
        console.error(`❌ API切换产品失败:`, switchResult);
        message.error($t("msg.switchProductFailed"), true);
        state.cardLoadingMap[id] = false;
      }
    } catch (error) {
      console.error(`❌ 产品切换异常:`, error);
      message.error($t("msg.switchProductFailed"), true);
      state.cardLoadingMap[id] = false;
    }
  } else {
    // 非直播状态的产品切换
    console.log(
      `🔄 非直播状态 - 切换到产品: ${state.productList.products[index]?.name || "未知产品"} (ID: ${id})`
    );

    state.selectedProductId = id;
    state.productDetail = state.productList.products[index] || {};
    state.cardLoadingMap[id] = false;
    try {
      setIOSSwitchLoading.call(this, id, false);
    } catch {}

    console.log(
      `✅ 产品切换完成 - 当前产品: ${state.productDetail.name || "未知产品"}`
    );
  }
}

// 执行待切换的产品切换
export async function executePendingProductSwitch(
  this: RootState & { [key: string]: any },
  roomId: string
) {
  const state = this.roomStates.get(roomId);
  if (!state || !state.pendingProductSwitch) {
    return;
  }

  const { productId, productIndex, switchParams, apiCompleted } =
    state.pendingProductSwitch;
  console.log(
    `🔄 执行待切换的产品切换: ${productId}, API已完成: ${apiCompleted}`
  );

  // 清除待切换状态
  state.pendingProductSwitch = null;

  try {
    let switchResult;

    if (apiCompleted) {
      // API已经调用过了，直接更新界面状态
      console.log(`🔄 API已完成，直接更新界面状态`);
      switchResult = { success: true };
    } else {
      // API还没调用，现在调用
      // console.log(`🔄 开始调用API切换产品`);
      switchResult = await switchProducts(switchParams);
      console.log(`🔍 API切换结果:`, switchResult);
    }

    if (switchResult && switchResult.success) {
      // API调用成功，更新本地状态
      // console.log(`✅ API切换产品成功`);

      // 彻底清理旧的Howler播放器和事件（强制清理，避免残留定时器与引用）
      if ((state as any).howlerPlayer) {
        const howlerPlayer = (state as any).howlerPlayer;
        try {
          if (typeof howlerPlayer.forceCleanupAllStates === "function") {
            howlerPlayer.forceCleanupAllStates();
          }
          // 新增：重置重复检测机制，确保新产品音频能正常添加
          if ((state as any).howlerEnqueuedIds) {
            (state as any).howlerEnqueuedIds.clear();
          }
          // 新增：重置当前产品音频的播放状态，避免被跳过逻辑过滤
          // 修复：使用最新的selectedProductId而不是可能过时的switchingProductId
          const currentPid = state.selectedProductId;
          if (currentPid && state.queue && state.queue.length > 0) {
            state.queue.forEach((item: any) => {
              if (item.productId === currentPid && item.status === "played") {
                item.status = "pending"; // 重置为待播放状态
                item.playedCharCount = 0; // 重置播放进度
                delete (item as any).audioTimeProgress; // 清除音频时间进度
              }
            });
            console.log(`已重置产品${currentPid}的音频播放状态`);
          }
          // 新增：清除产品切换标记，避免后续误判
          state.switchingProductId = null;
        } catch {}
        try {
          howlerPlayer.stop();
        } catch {}
        try {
          howlerPlayer.destroy();
        } catch {}
        (state as any).howlerPlayer = null;
        console.log(`旧播放器已彻底清理`);
      }

      // 清理UniversalWebAudio播放器的切换状态
      if ((state as any).universalWebAudioPlayer) {
        const universalPlayer = (state as any).universalWebAudioPlayer;
        console.log(`[UniversalWA] executePendingProductSwitch清理切换状态`);

        try {
          // 清理切换状态
          universalPlayer.clearSwitchState();
          // 清除产品限制
          universalPlayer.setAllowedProductId(null);
        } catch (error) {
          console.error(`[UniversalWA] 边界切换清理失败:`, error);
        }
      }

      // 清理所有可能的定时器和防抖状态
      if (state.lastLogTime) {
        state.lastLogTime = {};
      }

      // 🍎 保持 suppressAutoNext 生效，直到新产品音频真正投喂后再复位（避免 onended 抢先排旧内容）

      state.selectedProductId = productId;
      state.productDetail = state.productList.products[productIndex];

      try {
        (state as any).__switchGuardPid = productId;
        (state as any).__switchGuardVoiceNo = null;
      } catch {}

      // 🍎 保持 suppressAutoNext 生效，直到新产品音频真正投喂后再复位（避免 onended 抢先排旧内容）

      // 重置播放状态，但保留队列历史
      state.currentIndex = state.queue.length;
      state.isPlaying = false;

      // 清空保存的播放时间
      state.currentAudioTime = undefined;

      // 关键修复：清除队列中所有音频的播放进度，防止新产品音频被当作"恢复播放"模式
      console.log(`清除队列中所有音频的播放进度，确保新产品从头播放`);
      state.queue.forEach(item => {
        // 清除音频时间进度相关属性
        delete (item as any).audioTimeProgress;
        delete (item as any).audioCurrentTime;
        delete (item as any).lastSavedProgress;
        // 重置播放字符计数
        item.playedCharCount = 0;
        item.status = "pending";
      });

      try {
        const guardPid =
          (state as any).__switchGuardPid || state.selectedProductId || null;
        if (guardPid) {
          const guardStr = String(guardPid);
          const filtered = state.queue.filter((item: any) => {
            const pid = item?.productId || null;
            if (!pid) return true;
            if (Number(item?.voiceNo) === -1) return true;
            return pid === guardStr;
          });
          if (filtered.length !== state.queue.length) {
            console.log(
              `[queue-guard] removed ${state.queue.length - filtered.length} items for guardPid=${guardStr}`
            );
          }
          state.queue = filtered;
        }
      } catch (error) {
        console.warn("[queue-guard] purge error", error);
      }

      // 清除恢复保护期，允许接收新产品的音频
      state.lastRecoveryTime = null;

      // 设置产品切换标记，用于过滤旧产品的音频
      state.lastProductSwitchTime = Date.now();
      state.switchingProductId = productId;

      // 切换后重置去重集合，避免首条新产品音频被旧集合误杀
      try {
        (state as any).recentAudioIds = new Set<string>();
        (state as any).recentTextKeys = new Map<string, number>();
        const wa = (state as any).__appleWA__;
        try {
          if (wa) {
            // 修复：无论是否切换到同一产品，都清空去重集合和版本映射，确保重复音频能正常播放
            (wa as any).addedIds && (wa as any).addedIds.clear();
            (wa as any).idVersionMap && (wa as any).idVersionMap.clear();
            (wa as any).__dedupResetPid = null;
            // 确保重置suppressAutoNext标志，避免阻止边界修正和自动播放
            if (
              wa.player &&
              typeof wa.player.setSuppressAutoNext === "function"
            ) {
              wa.player.setSuppressAutoNext(false);
            }
            // 修复：重置版本追踪器，避免版本号跳跃导致音频被错误替换
            if ((wa as any).versionTracker) {
              (wa as any).versionTracker.clear();
            }
          }
        } catch {}
      } catch {}

      // 产品切换完成，继续正常播放流程
      // console.log(`🔄 产品切换完成，接受所有新音频`);

      // 确保清除冻结标志，避免边界修正被阻止
      try {
        // 通过全局状态获取并清除冻结标志
        const roomState = this.roomStates?.get(roomId);
        if (roomState) {
          // 清除Apple WA状态中的冻结标志
          const appleWA = (roomState as any).__appleWA__;
          if (appleWA) {
            appleWA.switchFreezeUntilTs = undefined;
            if (appleWA.player && typeof appleWA.player === "object") {
              (appleWA.player as any).suppressAttachUntilTs = undefined;
            }
          }
        }
      } catch (e) {
        console.warn("清除冻结标志失败:", e);
      }

      // 修复：检查队列中是否有未播放的音频，包括刚添加的新音频
      const hasUnplayedAudio = state.queue.length > state.currentIndex;
      const hasNewAudio =
        state.queue.length > 0 && state.currentIndex >= state.queue.length;

      // 注释详细的队列检查日志，保留简单统计
      // console.log(`🎵 产品切换后队列检查详情:`, {
      //   队列长度: state.queue.length,
      //   当前索引: state.currentIndex,
      //   正在播放: state.isPlaying,
      //   有未播放音频: hasUnplayedAudio,
      //   有新添加音频: hasNewAudio,
      //   队列最后一项:
      //     state.queue[state.queue.length - 1]?.text?.substring(0, 30) + "..."
      // });

      // 改为“存在可播项才播放”，避免重播队列末尾旧内容
      const PROTECT_MS2 = Number((state as any).switchProtectMs ?? 100);
      const inProtect2 =
        !!state.lastProductSwitchTime &&
        Date.now() - state.lastProductSwitchTime <= PROTECT_MS2;
      // 从队列头开始定位首个可播项
      let idx = 0;
      while (idx < state.queue.length) {
        const q: any = state.queue[idx];
        const currentPid =
          state.switchingProductId || state.selectedProductId || null;
        const sameProduct = !!(
          q?.productId &&
          currentPid &&
          q.productId === currentPid
        );
        const verMismatchRaw = q?.version !== (state.switchVersion ?? 0);
        const verMismatch = verMismatchRaw;
        const wrongProduct =
          state.switchingProductId &&
          q?.productId &&
          q.productId !== state.switchingProductId;
        // 保护期内仅对“无 productId”的消息使用时间阈值过滤
        const tooOldInProtect =
          inProtect2 &&
          !q?.productId &&
          q?.createTime &&
          q.createTime < state.lastProductSwitchTime!;
        const noUrl = !q?.audioUrl;
        if (verMismatch || wrongProduct || tooOldInProtect || noUrl) {
          idx++;
          continue;
        }
        break;
      }

      if (idx < state.queue.length && !state.isPlaying) {
        // console.log(`🎵 产品切换后发现可播项，推进索引到 ${idx} 并播放`);
        state.currentIndex = idx;

        const g: any = globalThis as any;
        const useUniversalWA = Boolean(
          (state as any).universalWebAudioPlayer ||
            (g &&
              g.__universalWebAudioRooms &&
              typeof g.__universalWebAudioRooms.has === "function" &&
              g.__universalWebAudioRooms.has(roomId))
        );

        if (useUniversalWA) {
          state.isPlaying = true;
          try {
            (state as any).__skipUniversalPlayId = state.queue[idx]?.id || null;
          } catch {}
        } else {
          state.isPlaying = true;
          const currentRoomId = state.roomId || this.currentRoomId;
          this.playNext(currentRoomId);
        }
      } else {
        // console.log(`🎵 产品切换后暂无可播项或正在播放中，等待新音频`);
      }

      // 检查WebSocket连接状态，只在连接异常时才重新连接
      if (
        !state.audioQueueWs ||
        state.audioQueueWs.readyState !== WebSocket.OPEN
      ) {
        console.warn(`🔌 WebSocket连接异常，需要重新连接`);
        if (state.audioQueueWs) {
          state.audioQueueWs.close();
          state.audioQueueWs = null;
        }

        await this.initAudioQueueWebSocket(roomId);
        console.log(`🔌 WebSocket重新连接完成`);
      } else {
        console.log(`🔌 WebSocket连接正常，保持现有连接`);
      }

      // 关键修复：只有在确认没有旧产品音频后才显示切换成功
      const PROTECT_MS3 = Number((state as any).switchProtectMs ?? 100);
      const inProtect3 =
        !!state.lastProductSwitchTime &&
        Date.now() - state.lastProductSwitchTime <= PROTECT_MS3;
      // 检查队列中是否还有旧产品音频需要过滤
      let hasOldProductAudio = false;
      for (let i = state.currentIndex; i < state.queue.length; i++) {
        const q: any = state.queue[i];
        const currentPid =
          state.switchingProductId || state.selectedProductId || null;
        const sameProduct = !!(
          q?.productId &&
          currentPid &&
          q.productId === currentPid
        );
        const verMismatchRaw = q?.version !== (state.switchVersion ?? 0);
        const verMismatch = verMismatchRaw && !sameProduct;
        const wrongProduct =
          state.switchingProductId &&
          q?.productId &&
          q.productId !== state.switchingProductId;
        const tooOldInProtect =
          inProtect3 &&
          !q?.productId &&
          q?.createTime &&
          q.createTime < state.lastProductSwitchTime!;
        const noUrl = !q?.audioUrl;
        if (verMismatch || wrongProduct || tooOldInProtect || noUrl) {
          hasOldProductAudio = true;
          break;
        }
      }

      if (hasOldProductAudio) {
        console.log(`🔄 检测到队列中还有旧产品音频，延迟显示切换成功弹窗`);
        // 标记需要延迟显示成功弹窗
        (state as any).pendingSwitchSuccessMessage = true;
      } else {
        console.log(`✅ 队列中无旧产品音频`);
        if (isAppleLike()) {
          // iOS：严格策略，仅在新产品真正起播时显示（此处仅置位）
          try {
            (state as any).pendingSwitchSuccessMessage = true;
          } catch {}
        } else {
          message.success($t("msg.switchProductSuccess"), true);
        }
      }

      console.log(
        `🎵 切换产品后请求新音频 - 产品: ${state.productDetail.name}`
      );
      if (isAppleLike()) {
        try {
          setIOSSwitchLoading.call(this, productId, true);
        } catch {}
      } else {
        state.cardLoadingMap[productId] = false;
      }
      // 切换成功后触发一次前台预填：可见态保守预填 1 条（隐藏态将由 onended 再补）
      try {
        (this as any).augmentStoreWithAppleHiddenWebAudio &&
          (this as any).augmentStoreWithAppleHiddenWebAudio(this as any);
        (this as any).prefillAppleWAHead &&
          (this as any).prefillAppleWAHead(roomId, 1);
      } catch {}
      // ✅ 切换成功：清理苹果 WA handoff/抑制与过滤（以防残留）
      try {
        const appleWA = (state as any).__appleWA__;
        const waPlayer = appleWA && appleWA.player;
        if (waPlayer) {
          if (typeof waPlayer.setSuppressAutoNext === "function")
            waPlayer.setSuppressAutoNext(false);
          if (typeof waPlayer.setForeHandoff === "function")
            waPlayer.setForeHandoff(false);
          if (typeof waPlayer.setAllowedProductId === "function") {
            const newProductId = state.selectedProductId || productId;
            waPlayer.setAllowedProductId(newProductId);
            console.log(
              "[switch] waPlayer set allowedProductId to",
              newProductId
            );
          }
        }
        const chain = (state as any).__appleHtml5Chain__;
        if (chain && typeof chain.setAllowedProductId === "function") {
          const newProductId = state.selectedProductId || productId;
          chain.setAllowedProductId(newProductId);
        }
      } catch {}
    } else {
      // API调用失败
      console.error(`❌ API切换产品失败:`, switchResult);
      message.error($t("msg.switchProductFailed"), true);
      state.cardLoadingMap[productId] = false;
      // ❌ 失败：清理 handoff/抑制与过滤，恢复连播
      try {
        const appleWA = (state as any).__appleWA__;
        const waPlayer = appleWA && appleWA.player;
        if (waPlayer) {
          if (typeof waPlayer.setSuppressAutoNext === "function")
            waPlayer.setSuppressAutoNext(false);
          if (typeof waPlayer.setForeHandoff === "function")
            waPlayer.setForeHandoff(false);
          if (typeof waPlayer.setAllowedProductId === "function")
            waPlayer.setAllowedProductId(undefined as any);
          console.log("[switch] waPlayer reset flags on failure");
        }
        const chain = (state as any).__appleHtml5Chain__;
        if (chain && typeof chain.setAllowedProductId === "function")
          chain.setAllowedProductId(undefined as any);
      } catch {}
    }
  } catch (error) {
    console.error(`❌ 产品切换异常:`, error);
    message.error($t("msg.switchProductFailed"), true);
    state.cardLoadingMap[productId] = false;
    // 异常：同样清理 handoff/抑制与过滤
    try {
      const appleWA = (state as any).__appleWA__;
      const waPlayer = appleWA && appleWA.player;
      if (waPlayer) {
        if (typeof waPlayer.setSuppressAutoNext === "function")
          waPlayer.setSuppressAutoNext(false);
        if (typeof waPlayer.setForeHandoff === "function")
          waPlayer.setForeHandoff(false);
        if (typeof waPlayer.setAllowedProductId === "function")
          waPlayer.setAllowedProductId(undefined as any);
        console.log("[switch] waPlayer reset flags on exception");
      }
      const chain = (state as any).__appleHtml5Chain__;
      if (chain && typeof chain.setAllowedProductId === "function")
        chain.setAllowedProductId(undefined as any);
    } catch {}
  }
}

export async function openProductSelector(
  this: RootState & { [key: string]: any }
) {
  if (!this.currentRoomState) {
    return;
  }

  if (this.currentRoomState.startLiveLoading) {
    return message.error($t("msg.liveStartingCannotSwitchProduct"), true);
  }

  if (this.currentRoomState.productList.status === "live") {
    return message.error($t("msg.liveStartingCannotSwitchProduct"), true);
  }

  // 确保 products 数组存在
  const currentProducts = Array.isArray(
    this.currentRoomState.productList.products
  )
    ? this.currentRoomState.productList.products
    : [];

  // 保存当前已选商品的ID列表，用于传递给选择器
  this.currentRoomState.selectedProductIds = currentProducts.map(p => p.id);

  // 保存原始状态，用于取消时恢复
  this._originalProductState = {
    products: [...currentProducts],
    selectedProductIds: [...this.currentRoomState.selectedProductIds],
    selectedProductId: this.currentRoomState.selectedProductId,
    productDetail: { ...this.currentRoomState.productDetail }
  };

  // 使用$patch来更新状态，避免代理陷阱问题
  this.$patch({
    showProductSelector: true
  });

  // 获取产品列表数据
  await this.fetchProductsForSelector();
}

export async function fetchProductsForSelector(
  this: RootState & { [key: string]: any }
) {
  const res = await listProducts({ pageNum: 1, pageSize: 1000 });

  if (res?.result?.page?.records) {
    this.$patch({
      products: res.result.page.records
    });
  } else {
    this.$patch({
      products: []
    });
  }
}

export function handleProductConfirm(
  this: RootState & { [key: string]: any },
  _selectedProducts: any[]
) {
  // 商品列表更新已在 updateSelectedProducts 中实时处理
  // 确认按钮只需关闭弹窗
  this.$patch({
    showProductSelector: false
  });
}

export function closeProductSelector(this: RootState & { [key: string]: any }) {
  this.$patch({
    showProductSelector: false
  });
}

export function handleProductCancel(this: RootState & { [key: string]: any }) {
  // 取消时恢复原始状态
  if (this._originalProductState && this.currentRoomState) {
    const state = this.currentRoomState;
    const original = this._originalProductState;

    // 确保 productList 存在
    if (!state.productList) {
      state.productList = {
        id: "",
        name: "",
        products: [],
        status: "draft"
      };
    }

    state.productList.products = Array.isArray(original.products)
      ? [...original.products]
      : [];
    state.selectedProductIds = Array.isArray(original.selectedProductIds)
      ? [...original.selectedProductIds]
      : [];
    state.selectedProductId = original.selectedProductId;
    state.productDetail = { ...original.productDetail };
  }

  this.$patch({
    showProductSelector: false
  });
}

export function updateSelectedProducts(
  this: RootState & { [key: string]: any },
  selectedIds: string[],
  availableProducts?: any[]
) {
  if (!this.currentRoomId) return;

  // 直接从Map中获取房间状态，确保反应性
  const state = this.roomStates.get(this.currentRoomId);
  if (!state) return;

  // 优先使用传入的产品列表，否则使用全局产品列表
  const globalProducts = Array.isArray(availableProducts)
    ? availableProducts
    : Array.isArray(this.products)
      ? this.products
      : [];

  // 根据ID从全局商品列表中找到对应的商品
  const selectedProducts = selectedIds
    .map(id => globalProducts.find(p => p && p.id === id))
    .filter(Boolean); // 过滤掉找不到的商品

  // 确保 productList 存在
  if (!state.productList) {
    state.productList = { products: [], status: "draft" };
  }

  // 立即更新房间的商品列表显示
  state.productList.products = [...selectedProducts];
  state.selectedProductIds = [...selectedIds];

  // 如果当前选中的商品不在新列表中，选择第一个商品
  if (selectedProducts.length > 0) {
    const currentSelectedExists = selectedProducts.find(
      p => p.id === state.selectedProductId
    );
    if (!currentSelectedExists) {
      // 当前选中的商品不在新列表中，选择第一个商品
      state.selectedProductId = selectedProducts[0].id;
      state.productDetail = selectedProducts[0];
    }
  } else {
    // 没有选中任何商品，清空当前选中状态
    state.selectedProductId = "";
    state.productDetail = {};
  }
}
